import { Form, Accordion } from "react-bootstrap";
import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import { Input, MultiSelect, PageWrapper } from "../../components";
import { useTranslation } from "react-i18next";
import { Pages, DriverType } from '../../shared/enums'
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getVehicleTypeList, getVehicleTypeDetail, createVehicleType, updateVehicleType } from "../../api/vehicle-type";
import { RiEdit2Fill, RiAddLargeLine } from "react-icons/ri";
import Button from '../../components/common/Button';
import { DEFAULT_VALUES, DEFAULT_FARE, DEFAULT_PEAK_HOUR_CHARGES } from "../../shared/constants/fare";
import { camelToUpperSnake } from "../../shared/utils";
import { isEmpty, omit } from "lodash";

const VehicleTypeDetail = ({ }) => {
    const { t } = useTranslation()
    const form = useForm({ defaultValues: { ...DEFAULT_VALUES } })
    const navigate = useNavigate()
    const { id } = useParams()
    const [isEdit, setIsEdit] = useState(false)
    const [vehicleTypes, setVehicleTypes] = useState([])
    const [btnLoading, setBtnLoading] = useState(false)

    useEffect(() => {
        if (id !== "new") {
            setIsEdit(true)
            getData()
        } else {
            getVehicleTypes()
        }
    }, [])

    const toTimeString = (timeObj) =>
        `${timeObj.hour.toString().padStart(2, '0')}:${timeObj.minute.toString().padStart(2, '0')}`;

    const toTimeObject = (timeString) => {
        const [hour, minute] = timeString.split(':').map(Number);
        return { hour, minute };
    };

    const getVehicleTypes = async (vehicleType = null) => {
        const response = await getVehicleTypeList({ skip: 0, limit: 1000 })
        const options = response.items.filter(vehicleType => vehicleType.id != id).map(vehicleType => ({ label: vehicleType.name, value: vehicleType.id }))
        setVehicleTypes(options)
        if (vehicleType) {
            const fareData = vehicleType.fare || {};
            form.reset({
                name: vehicleType.name,
                crossVehicles: options.filter(e => vehicleType.crossVehicles.includes(e.value)),
                description: fareData.name || '',
                ...omit(fareData, ['_id', 'id', 'driverType', 'vehicleType', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', '__v', 'success', 'name']),
                peakHourCharges: (fareData.peakHourCharges || []).map(e => ({
                    fromTime: toTimeString(e.fromTime),
                    toTime: toTimeString(e.toTime),
                    pricePerDistance: e.pricePerDistance,
                    pricePerMinute: e.pricePerMinute,
                }))
            })
        }
    }

    const getData = async () => {
        const response = await getVehicleTypeDetail(id)
        getVehicleTypes(response)
    }

    const create = async (data) => {
        setBtnLoading(true)
        const fareData = omit(data, ['name', 'crossVehicles', 'description']);
        const response = await createVehicleType({
            // Vehicle type fields
            name: data.name,
            crossVehicles: (data.crossVehicles ?? []).map(e => e.value),
            // Fare fields flattened at the same level
            ...fareData,
            description: data.description, // Fare name/description
            driverType: DriverType.E_HAILING,
            peakHourCharges: (data.peakHourCharges || []).map(e => ({
                pricePerDistance: e.pricePerDistance,
                pricePerMinute: e.pricePerMinute,
                fromTime: toTimeObject(e.fromTime),
                toTime: toTimeObject(e.toTime)
            }))
        })
        if (response.success) navigate(Pages.VehicleType)
        setBtnLoading(false)
    }

    const update = async (data) => {
        setBtnLoading(true)
        const fareData = omit(data, ['name', 'crossVehicles', 'description']);
        const response = await updateVehicleType(id, {
            // Vehicle type fields
            name: data.name,
            crossVehicles: (data.crossVehicles ?? []).map(e => e.value),
            // Fare fields flattened at the same level
            ...fareData,
            description: data.description, // Fare name/description
            driverType: DriverType.E_HAILING,
            peakHourCharges: (data.peakHourCharges || []).map(e => ({
                pricePerDistance: e.pricePerDistance,
                pricePerMinute: e.pricePerMinute,
                fromTime: toTimeObject(e.fromTime),
                toTime: toTimeObject(e.toTime)
            }))
        })
        if (response.success) navigate(Pages.VehicleType)
        setBtnLoading(false)
    }

    return <PageWrapper
        headerProps={{
            title: isEdit ? "EDIT_VEHICLE_TYPE" : "ADD_VEHICLE_TYPE",
            rightElement: <Button
                type="submit"
                variant="primary"
                icon={isEdit ? <RiEdit2Fill /> : <RiAddLargeLine />}
                loading={btnLoading}
                onClick={() => form.handleSubmit(isEdit ? update : create)()}
            >
                {t(isEdit ? "UPDATE" : "CREATE")}
            </Button>
        }}
    >
        <FormProvider {...form}>
            <Form>
                <Input label={t("NAME")} name={"name"} rules={{ required: t("NAME_IS_REQUIRED") }} />
                <MultiSelect
                    label={t("CROSS_VEHICLES")}
                    name={"crossVehicles"}
                    options={vehicleTypes}
                />
                <Input label={t("DESCRIPTION")} name={"description"} type="textarea" />
                {Object.entries(isEdit ? omit(form.getValues(), ['name', 'crossVehicles', 'description']) : DEFAULT_VALUES).map(([key, value]) => <ObjectFields key={key} control={form.control} title={key} data={value} />)}
            </Form>
        </FormProvider>
    </PageWrapper>
}

const generateContent = (control, t, parent, content) => {
    if (typeof content !== 'object' || content === null) {
        return null;
    }

    if (Array.isArray(content)) {
        return <ArrayFields control={control} fieldName={parent} />
    }

    return Object.entries(content).map(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
            return (
                <Accordion key={key} defaultActiveKey={'0'} className="mt-4 mb-4">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>{t(camelToUpperSnake(key))}</Accordion.Header>
                        <Accordion.Body >
                            {generateContent(control, t, isEmpty(parent) ? key : parent + "." + key, value)}
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            );
        }

        if (key === "fromTime" || key === "toTime") {
            return <Input
                key={key}
                name={parent + "." + key}
                label={t(camelToUpperSnake(key))}
                type="time"
                rules={{ required: t("CANT_BE_EMPTY") }}
            />
        }

        return (
            <Input
                key={key}
                name={parent + "." + key}
                label={t(camelToUpperSnake(key))}
                rules={{ required: t("CANT_BE_EMPTY") }}
            />
        );
    });
};

const ObjectFields = ({ control, title, data }) => {
    const { t } = useTranslation()

    return <>
        <Accordion defaultActiveKey={'0'} className="mb-4">
            <Accordion.Item eventKey="0">
                <Accordion.Header>{t(camelToUpperSnake(title))}</Accordion.Header>
                <Accordion.Body>
                    {generateContent(control, t, title, data)}
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    </>
}

const ArrayFields = ({ control, fieldName }) => {
    const { t } = useTranslation()
    const { fields, append, remove } = useFieldArray({ control, name: fieldName });

    return <>
        {
            fields.map((field, index) => <Accordion key={field.id} defaultActiveKey={'0'} className="mb-4">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        <div className="w-100 d-flex justify-content-between align-items-center me-3">
                            #{t(camelToUpperSnake(fieldName))} {index + 1}
                            <Button variant="danger" size="sm" className="p-0 px-2" style={{ height: "27px" }} onClick={(e) => {
                                e.stopPropagation()
                                remove(index)
                            }}>
                                {t("REMOVE")}
                            </Button>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        {generateContent(control, t, isEmpty(fieldName) ? fieldName : fieldName + "." + index, omit(field, ['id']))}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            )
        }
        <Button onClick={() => append(fieldName === "fares" ? DEFAULT_FARE : DEFAULT_PEAK_HOUR_CHARGES)} >{t("ADD")}</Button>
    </>
}

export default VehicleTypeDetail;